/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponentProps, IGridSettings, IImageSettings, Image } from '@msdyn365-commerce/core';
import { ProductPrice, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getFallbackImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import * as React from 'react';

import { Modal, ModalBody, ModalFooter, ModalHeader, Button, getPayloadObject, getTelemetryAttributes, ITelemetryContent, TelemetryConstant } from '@msdyn365-commerce-modules/utilities';

export interface IPopupProps extends IComponentProps<{ product: SimpleProduct | undefined; price: ProductPrice | undefined }> {
    dialogStrings?: {
        goToCartText: string;
        continueShoppingText: string;
        headerItemOneText: string;
        headerItemFormatText: string;
        headerMessageText: string;
        freePriceText: string;
        originalPriceText: string;
        currentPriceText: string;
    };

    className: string;
    imageSettings?: IImageSettings;
    gridSettings: IGridSettings | undefined;
    productQuantity: number;
    priceComponent: React.ReactNode;
    navigationUrl?: string;
    modalOpen: boolean | undefined;
    telemetryContent?: ITelemetryContent;
    setModalOpen(newValue: boolean): void;
}

export const Popup = (popupProps: IPopupProps) => {
    const {
        dialogStrings,
        className,
        imageSettings,
        gridSettings,
        data: { product, price },
        productQuantity,
        priceComponent,
        navigationUrl,
        modalOpen,
        telemetryContent,
        setModalOpen,
        context
    } = popupProps;
    const closeModal = React.useCallback(() => {
        setModalOpen(false);
    }, [setModalOpen]);
    if (!dialogStrings || !product) {
        return null;
    }
    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.ContinueShopping);
    const continueShoppingAttribute = getTelemetryAttributes(telemetryContent!, payLoad);
    payLoad.contentAction.etext = TelemetryConstant.ViewBagAndCheckout;
    const viewBagAttribute = getTelemetryAttributes(telemetryContent!, payLoad);

    const fallbackImageUrl = getFallbackImageUrl(
        product.ItemId,
        context.actionContext.requestContext.apiSettings,
        context.request.app?.config?.OmniChannelMedia
    );
    if (imageSettings) {
        imageSettings.cropFocalRegion = true;
    }
    const quantity = productQuantity !== undefined ? productQuantity : 1;
    const itemsString =
        quantity === 1 ? dialogStrings.headerItemOneText : dialogStrings.headerItemFormatText.replace('{0}', quantity.toString());
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- app configs are of generic type
    const shouldDisplayUnitOfMeasure =
        context.app.config?.unitOfMeasureDisplayType && context.app.config.unitOfMeasureDisplayType !== 'none';

    const reducedDimensions: string = product.Dimensions
        ? product.Dimensions.reduce<string>((str, productDimension) => {
              if (productDimension.DimensionValue && productDimension.DimensionValue.Value) {
                  if (str) {
                      return `${str}, ${productDimension.DimensionValue.Value}`;
                  }

                  return `${productDimension.DimensionValue.Value}`;
              }

              return str;
          }, '')
        : '';

        let productShorDescription : string = '';

        if(product && product.ExtensionProperties && typeof product.ExtensionProperties !== 'undefined')
        {
            const productDescriptionProp = product.ExtensionProperties.filter(x=> x.Key === 'GCProductDescription');
            if(productDescriptionProp && productDescriptionProp[0] && productDescriptionProp[0].Value !== undefined)
            {
                productShorDescription= productDescriptionProp[0].Value.StringValue !== undefined ? productDescriptionProp[0].Value.StringValue : '';
            }
        }

    return (
        <Modal isOpen={modalOpen} onClosed={closeModal} className={`${className}__dialog`} toggle={closeModal}>
            <ModalHeader toggle={closeModal} className={`${className}__dialog__header`}>
                <span>{itemsString}</span>
                <span>{dialogStrings.headerMessageText}</span>
            </ModalHeader>
            <ModalBody className={`${className}__dialog__body`}>
                <Image
                    requestContext={context.actionContext.requestContext}
                    className={`${className}__dialog__image`}
                    src={product.PrimaryImageUrl || ''}
                    fallBackSrc={fallbackImageUrl}
                    altText={product.ItemId + ': ' + product.Name}
                    gridSettings={gridSettings || {}}
                    imageSettings={imageSettings}
                    loadFailureBehavior="empty"
                />
                <span className={`${className}__dialog__product-title`}>{product.ItemId + ': ' + product.Name}</span>
                {reducedDimensions !== '' ? <div className={`${className}__dialog__product-variants`}>{reducedDimensions}</div> : ''}
                <div className={`msc-buybox__description-section`}>{productShorDescription}</div>
                {price ? <div className={`${className}__dialog__product-price`}>{priceComponent}</div> : ''}
                {product.DefaultUnitOfMeasure && shouldDisplayUnitOfMeasure ? (
                    <div className={`${className}__dialog__product-unitOfMeasure`}>
                        <span>{product.DefaultUnitOfMeasure}</span>
                    </div>
                ) : (
                    ''
                )}
            </ModalBody>
            <ModalFooter className={`${className}__dialog__footer`}>
                <Button
                    className={`${className}__dialog__go-to-cart`}
                    href={navigationUrl}
                    aria-label={dialogStrings.goToCartText}
                    {...viewBagAttribute}
                >
                    {dialogStrings.goToCartText}
                </Button>
                <Button
                    className={`${className}__dialog__back-to-shopping`}
                    onClick={closeModal}
                    aria-label={dialogStrings.continueShoppingText}
                    {...continueShoppingAttribute}
                >
                    {dialogStrings.continueShoppingText}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default Popup;
